import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef, GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import moment from 'moment-timezone';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { ShiftsService } from 'src/app/_services/shifts.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { ModalServiceService } from 'src/app/_services/modal-service.service';
import { DeleteRecordService } from 'src/app/_services/delete-record.service';
import { UtilServiceService } from 'src/app/_services/utilService/util-service.service';
import { PermissionService } from 'src/app/auth/permission.service';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import { ButtonRendererComponent } from 'src/app/renderer/button-renderer.component';
import { TableHeaderToolTipComponent } from 'src/app/shared/table-header-tool-tip/table-header-tool-tip.component';

@Component({
  selector: 'app-user-shifts',
  standalone: false,
  templateUrl: './users-shifts.component.html',
  styleUrl: './users-shifts.component.scss',
})
export class UsersShiftsComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  @Input() userId: any;
  @Input() profileId: any;
  @Input() profile: any;

  translate: any;
  gridApi!: GridApi;
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef;
  frameworkComponents: any;
  gridOptions: any;
  overlayNoRowsTemplate = '';
  rowData: any[] = [];
  totalRecord = 0;
  noRecords = false;
  searchValue: any = null;
  searchValueData = false;
  gridParams: any;

  create = false;
  update = false;
  remove = false;
  manageShifts = false;

  constructor(
    private languageTranslateService: LanguageTranslateService,
    private shiftsService: ShiftsService,
    private spinner: NgxSpinnerService,
    private errorUtil: ErrorUtil,
    private modalService: ModalServiceService,
    private deleteRecordService: DeleteRecordService,
    private router: Router,
    private util: UtilServiceService,
    private perm: PermissionService,
    private auth: AuthenticationService,
  ) {
    super();

    this.push(
      this.shiftsService.refreshList$.subscribe(() => {
        this.loadAssignments();
      })
    );

    this.frameworkComponents = {
      buttonRenderer: ButtonRendererComponent,
    };

    this.gridOptions = {
      rowModelType: 'clientSide',
      pagination: true,
      paginationPageSize: 25,
      sortingOrder: ['desc', 'asc'],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      sortable: true,
      resizable: true,
      filter: false,
      suppressHeaderMenuButton: true,
      tooltipComponent: TableHeaderToolTipComponent,
    };
  }

  ngOnInit(): void {
    this.manageShifts = this.perm.capable('manage_shifts', 'read');
    this.create = this.perm.capable('manage_shifts', 'create');
    this.update = this.perm.capable('manage_shifts', 'update');
    this.remove = this.perm.capable('manage_shifts', 'remove');
    this.getLanguageData();
  }

  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          this.overlayNoRowsTemplate =
            "<div class='not-found'><span>" +
            (this.translate?.general?.messages?.noRecordsFound || 'Record not found') +
            '</span></div>';
          this.initializeColumns();
          this.loadAssignments();
        }
      })
    );
  }

  initializeColumns() {
    this.columnDefs = [
      {
        headerName: 'Shift Type',
        field: 'shiftType',
        width: 180,
        headerTooltip: '#',
        cellClass: () => [ 'text-1-5 font-weight-bold table_default_color'],
      },
      {
        headerName: 'Eff. Start',
        field: 'effStart',
        width: 130,
        headerTooltip: '#',
      },
      {
        headerName: 'Eff. End',
        field: 'effEnd',
        width: 130,
        headerTooltip: '#',
      },
      {
        headerName: 'Source',
        field: 'sourceLabel',
        width: 180,
        headerTooltip: '#',
      },
      {
        headerName: this.translate?.general?.buttons?.status?.singular || 'Status',
        field: 'status',
        width: 120,
        headerTooltip: '#',
        cellRenderer: (params: any) => {
          if (!params.data) {
            return '';
          }
          if (params.data.status === 'active') {
            return (
              '<span class="p-mr-2 p-tag p-component p-tag-success"><span class="p-tag-value">' +
              (this.translate?.general?.status?.active?.singular || 'Active') +
              '</span></span>'
            );
          }
          return (
            '<span class="p-mr-2 p-tag p-component p-tag-danger"><span class="p-tag-value text-dark">' +
            (this.translate?.general?.status?.inactive?.singular || 'Inactive') +
            '</span></span>'
          );
        },
      },
      {
        headerName: this.translate?.general?.buttons?.action?.singular || 'Action',
        colId: 'action',
        filter: false,
        sortable: false,
        headerClass: 'marging-auto hide-action-border',
        width: 120,
        cellStyle: { 'text-align': 'center' },
        cellRenderer: 'buttonRenderer',
        headerTooltip: '#',
        cellRendererParams: {
          onClick: this.onRowAction.bind(this),
          permissions: { update: this.update, remove: this.remove },
        },
      },
    ];

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
      setTimeout(() => this.gridApi.sizeColumnsToFit(), 300);
    }
  }

  onGridReady(params: any) {
    this.gridParams = params;
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
    if (this.columnDefs?.length) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
    }
    if (this.rowData?.length) {
      this.gridApi.setGridOption('rowData', this.rowData);
    }
  }

  loadAssignments() {
    const id = this.userId || this.profileId || this.profile?.id;
    if (!id) {
      this.rowData = [];
      this.totalRecord = 0;
      this.noRecords = true;
      return;
    }

    this.spinner.show();
    this.push(
      this.shiftsService.getApplicableShiftAssignments(id).subscribe(
          (res: any) => {
            this.spinner.hide();
            const body = res?.body ?? res ?? {};
            const list = body.content ?? body.data ?? (Array.isArray(body) ? body : []);
            this.rowData = (Array.isArray(list) ? list : []).map((item: any, index: number) =>
              this.mapRow(item, index)
            );
            this.totalRecord = this.rowData.length;
            this.noRecords = this.totalRecord === 0;
            this.searchValue = null;
            this.searchValueData = false;

            if (this.gridApi) {
              this.gridApi.setGridOption('quickFilterText', '');
              this.gridApi.setGridOption('rowData', this.rowData);
              if (this.noRecords) {
                this.gridApi.showNoRowsOverlay();
              } else {
                this.gridApi.hideOverlay();
              }
              setTimeout(() => this.gridApi.sizeColumnsToFit(), 200);
            }
          },
          (err: any) => {
            this.spinner.hide();
            this.rowData = [];
            this.totalRecord = 0;
            this.noRecords = true;
            if (err?.status === 204) {
              if (this.gridApi) {
                this.gridApi.setGridOption('rowData', []);
                this.gridApi.showNoRowsOverlay();
              }
              return;
            }
            this.errorUtil.setErrorMessage(
              err.status,
              err.error ? err.error.title : null,
              err.error ? err.error.title : null,
              'error',
              2000
            );
          }
        )
    );
  }

  /********************************** Search **************************/
  quickSearch() {
    if (!this.gridApi) {
      return;
    }
    const value = this.searchValue || '';
    if (value.length > 0) {
      this.searchValueData = true;
      this.gridApi.setGridOption('quickFilterText', value);
      this.totalRecord = this.gridApi.getDisplayedRowCount();
      if (this.totalRecord === 0) {
        this.gridParams?.api?.showNoRowsOverlay();
      } else {
        this.gridParams?.api?.hideOverlay();
      }
    } else {
      this.gridApi.setGridOption('quickFilterText', '');
      this.totalRecord = this.gridApi.getDisplayedRowCount();
      this.gridParams?.api?.hideOverlay();
    }
  }

  onSearchCheck() {
    if (!this.searchValue || this.searchValue.length === 0) {
      this.searchValueData = false;
      this.searchValue = '';
      this.quickSearch();
    }
  }

  onClear() {
    this.searchValueData = false;
    this.searchValue = '';
    this.quickSearch();
  }

  private mapRow(item: any, index: number) {
    const statusRaw = String(item.shiftTypeStatus || 'ACTIVE').toUpperCase();
    const start = item.effectiveStartDate || item.effStart || item.startDate;
    const end = item.effectiveEndDate || item.effEnd || item.endDate;
    const assigneeType = String(item.assigneeType || 'USER').toUpperCase();
    const isGroup = assigneeType === 'USER_GROUP' || assigneeType === 'GROUP';
    const groupName = item.userGroupName || item.groupName || item.sourceGroupName;
    const rawId = item.id ?? item.assignmentId;

    return {
      id: rawId != null && rawId !== '' ? rawId : `row-${index}`,
      shiftTypeId: item.shiftTypeId ?? item.shiftType?.id ?? null,
      shiftType:
        item.shiftTypeName ||
        item.shiftType?.name ||
        '—',
      effStart: this.formatDisplayDate(start),
      effEnd: this.formatDisplayDate(end),
      sourceLabel: isGroup ? (groupName ? `Group: ${groupName}` : 'Group') : 'Individual',
      status: statusRaw === 'ACTIVE' ? 'active' : 'inactive',
      hideEditDeleteButton: isGroup ? 'yes' : 'no',
      raw: item,
    };
  }

  private formatDisplayDate(value: any): string {
    if (!value) {
      return '—';
    }
    const m = moment.tz(value, this.auth.getUserTimezone());
    if (!m.isValid()) {
      return String(value);
    }
    const dateFormat = (localStorage.getItem('date_format') || 'YYYY-MM-DD').toUpperCase();
    return m.format(dateFormat);
  }

  onRowAction(e: any) {
    if (e.action === 'edit') {
      const row = e.rowData?.raw || e.rowData || {};
      const id = row.assignmentId ?? row.id ?? e.rowData?.id;
      if (id == null || id === '' || String(id).startsWith('row-')) {
        return;
      }
      this.modalService.openModal('assignShift', {
        ...row,
        id,
        assignmentId: id,
        userId: row.userId ?? row.user?.id ?? (this.userId || this.profileId || this.profile?.id),
        viewMode: 'users',
        lockUser: true,
        user: this.profile,
        fullName:
          this.profile?.fullName ||
          [this.profile?.firstName, this.profile?.lastName].filter(Boolean).join(' ').trim() ||
          null,
      });
    } else if (e.action === 'delete') {
      const raw = e.rowData?.raw || e.rowData || {};
      const id = raw.assignmentId ?? raw.id ?? e.rowData?.id;
     
      this.deleteRecordService.getItems({
        ...raw,
        id,
        deleteType: 'Shift Assignment',
        deleteHeader: 'Shift Assignment',
        message:
          (this.translate?.general?.messages?.confirmDelete ||
            'Are you sure you want to delete') + '?',
      });
    }
  }

  openAssign() {
    const id = this.userId || this.profileId || this.profile?.id;
    if (id == null || id === '') {
      return;
    }
    const fullName =
      this.profile?.fullName ||
      [this.profile?.firstName, this.profile?.lastName].filter(Boolean).join(' ').trim() ||
      null;
    this.modalService.openModal('assignShift', {
      viewMode: 'users',
      userId: id,
      lockUser: true,
      user: this.profile,
      fullName,
    });
  }

  getRefresh() {
    this.loadAssignments();
  }

  public getContextMenuItems = (params: any) => {
    params.node.data.field = params.column.colId;
    const results: any[] = [];
    if (params.column.colId === 'shiftType' && params.node.data?.shiftTypeId) {
      const data = params.node.data;
      results.push({
        name: 'Open link in new tab',
        action: () => this.redirectToNewTab(data),
      });
    }
    return results;
  };

  private redirectToNewTab(data: any) {
    if (data.field === 'shiftType' && data.shiftTypeId != null) {
      let url = this.router.serializeUrl(
        this.router.createUrlTree(['dashboard/setup/shifts/details'], {
          queryParams: { id: data.shiftTypeId },
        })
      );
      url = url.replace('%23', '#');
      this.util.openItemToNewTab(url);
    }
  }

  onShiftTypeClicked(e: any) {
    const colId = e.colDef?.colId || e.colDef?.field;
    if (colId === 'shiftType' && e.data?.shiftTypeId) {
      this.router.navigate(['dashboard/setup/shifts/details'], {
        queryParams: { id: e.data.shiftTypeId },
      });
    }
  }

  push(obs: any) {
    super.push(obs);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
